import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Modal, InputGroup, Alert, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { Plus, Edit, Trash2, Bell, Search, AlertTriangle, CheckCircle, MessageSquare } from 'lucide-react';
import { getNotificationSubscriptions, createNotificationSubscription, updateNotificationSubscription, deleteNotificationSubscription } from '../services/notifications';

const Notifications = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    webhook_url: '',
    is_active: true,
    events: ['error', 'warning', 'info']
  });
  const [submitting, setSubmitting] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);

  // Fetch subscriptions from API
  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await getNotificationSubscriptions();
      setSubscriptions(response);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      showToast('Failed to load subscriptions: ' + (error.message || error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const getPlatformIcon = () => <MessageSquare size={16} />;

  const filteredSubscriptions = subscriptions.filter(sub => {
    return sub.webhook_url.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);
    setFormData({
      webhook_url: subscription.webhook_url,
      is_active: subscription.is_active,
      events: subscription.event_types || ['error', 'warning', 'info']
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingSubscription(null);
    setFormData({
      webhook_url: '',
      is_active: true,
      events: ['error', 'warning', 'info']
    });
    setShowModal(true);
  };

  const handleDeleteClick = (subscription) => {
    setSubscriptionToDelete(subscription);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (subscriptionToDelete) {
      try {
        await deleteNotificationSubscription(subscriptionToDelete.id);
        fetchSubscriptions();
        showToast('Subscription deleted successfully');
      } catch (error) {
        console.error('Failed to delete subscription:', error);
        showToast('Failed to delete subscription: ' + (error.message || error), 'danger');
      } finally {
        setShowDeleteModal(false);
        setSubscriptionToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSubscriptionToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingSubscription) {
        await updateNotificationSubscription(editingSubscription.id, formData);
        showToast('Subscription updated successfully');
      } else {
        await createNotificationSubscription(formData);
        showToast('Subscription created successfully');
      }
      setShowModal(false);
      fetchSubscriptions();
    } catch (error) {
      console.error('Failed to save subscription:', error);
      showToast('Failed to save subscription: ' + (error.message || error), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEventToggle = (event) => {
    setFormData(prev => {
      const events = prev.events || [];
      if (events.includes(event)) {
        return {
          ...prev,
          events: events.filter(e => e !== event)
        };
      } else {
        return {
          ...prev,
          events: [...events, event]
        };
      }
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading notifications...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={toastShow} 
          onClose={() => setToastShow(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white justify-content-between`}>
            <div className="d-flex align-items-center">
              <CheckCircle size={18} className="me-2" />
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={() => setToastShow(false)}
              aria-label="Close"
            ></button>
          </Toast.Header>
          <Toast.Body className="bg-light">
            <div className="d-flex align-items-center">
              <div className="toast-message flex-grow-1">
                {toastMessage}
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">Discord Notifications</h1>
          <p className="text-muted mb-0">
            Configure Discord webhook notifications for system events and alerts
          </p>
        </div>
        <Button variant="primary" onClick={handleAdd}>
          <Plus size={16} className="me-2" />
          Add Webhook
        </Button>
      </div>

      {/* Info Alert */}
      <Alert variant="info" className="mb-4">
        <AlertTriangle size={20} className="me-2" />
        Configure Discord webhook notifications to receive alerts about system events, errors, and important activities.
      </Alert>

      {/* Stats Cards */}
      <Row className="g-3 mb-4">
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Bell size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{subscriptions.length}</h5>
              <p className="text-muted mb-0 small">Total Webhooks</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-success mb-2" style={{ fontSize: '1.5rem' }}>✅</div>
              <h5 className="fw-bold mb-0">{subscriptions.filter(s => s.is_active).length}</h5>
              <p className="text-muted mb-0 small">Active</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <MessageSquare size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{subscriptions.length}</h5>
              <p className="text-muted mb-0 small">Discord</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-warning mb-2" style={{ fontSize: '1.5rem' }}>📊</div>
              <h5 className="fw-bold mb-0">
                {subscriptions.reduce((total, sub) => total + (sub.event_types?.length || 0), 0)}
              </h5>
              <p className="text-muted mb-0 small">Total Events</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-2">
            <Col xs={12} md={8}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Discord webhooks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <div className="text-muted small d-flex align-items-center h-100 justify-content-end justify-content-md-start">
                {filteredSubscriptions.length} webhook{filteredSubscriptions.length !== 1 ? 's' : ''}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Subscriptions Table - Desktop */}
      <div className="d-none d-md-block">
        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="mb-0" hover>
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 py-3 px-4">Platform</th>
                    <th className="border-0 py-3">Webhook URL</th>
                    <th className="border-0 py-3">Events</th>
                    <th className="border-0 py-3">Status</th>
                    <th className="border-0 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.map((subscription) => (
                    <tr key={subscription.id}>
                      <td className="px-4 py-3">
                        <Badge bg="primary" className="d-flex align-items-center w-fit">
                          <span className="me-1">{getPlatformIcon()}</span>
                          Discord
                        </Badge>
                      </td>
                      <td className="py-3">
                        <code className="small text-truncate d-block" style={{ maxWidth: '200px' }}>
                          {subscription.webhook_url}
                        </code>
                      </td>
                      <td className="py-3">
                        <div className="d-flex flex-wrap gap-1">
                          {subscription.event_types?.map((event, index) => (
                            <Badge key={index} bg="secondary" className="small">
                              {event}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge bg={subscription.is_active ? 'success' : 'secondary'}>
                          {subscription.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(subscription)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDeleteClick(subscription)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {filteredSubscriptions.length === 0 && (
              <div className="text-center py-5 text-muted">
                {searchTerm ? 'No webhooks found matching your search.' : 'No Discord webhooks configured yet.'}
                {!searchTerm && (
                  <div className="mt-2">
                    <Button variant="outline-primary" size="sm" onClick={handleAdd}>
                      Add your first webhook
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Subscriptions Cards - Mobile */}
      <div className="d-md-none">
        {filteredSubscriptions.map((subscription) => (
          <Card key={subscription.id} className="mb-3 shadow-sm border-0">
            <Card.Body className="p-3">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="d-flex align-items-center">
                  <Badge bg="primary" className="me-2">
                    {getPlatformIcon()}
                  </Badge>
                  <span className="fw-medium">Discord Webhook</span>
                </div>
                <Badge bg={subscription.is_active ? 'success' : 'secondary'}>
                  {subscription.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">Webhook URL</small>
                <code className="small text-break">{subscription.webhook_url}</code>
              </div>

              <div className="mb-3">
                <small className="text-muted d-block mb-1">Events</small>
                <div className="d-flex flex-wrap gap-1">
                  {subscription.event_types?.map((event, index) => (
                    <Badge key={index} bg="secondary" className="small">
                      {event}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleEdit(subscription)}
                  className="flex-fill"
                >
                  <Edit size={14} className="me-1" />
                  Edit
                </Button>
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => handleDeleteClick(subscription)}
                  className="flex-fill"
                >
                  <Trash2 size={14} className="me-1" />
                  Delete
                </Button>
              </div>
            </Card.Body>
          </Card>
        ))}
        {filteredSubscriptions.length === 0 && (
          <Card className="text-center py-5 text-muted">
            <Card.Body>
              {searchTerm ? 'No webhooks found matching your search.' : 'No Discord webhooks configured yet.'}
              {!searchTerm && (
                <div className="mt-3">
                  <Button variant="primary" onClick={handleAdd}>
                    <Plus size={16} className="me-2" />
                    Add First Webhook
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            {editingSubscription ? (
              <>
                <Edit size={20} className="me-2 text-primary" />
                Edit Discord Webhook
              </>
            ) : (
              <>
                <Plus size={20} className="me-2 text-primary" />
                Add Discord Webhook
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-0">
            <Row className="g-3">
              <Col xs={12}>
                <Alert variant="info" className="py-2">
                  <div className="d-flex align-items-center">
                    <MessageSquare size={16} className="me-2" />
                    <span>This webhook will send notifications to your Discord server</span>
                  </div>
                </Alert>
              </Col>
              
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Discord Webhook URL</Form.Label>
                  <Form.Control
                    type="url"
                    placeholder="https://discord.com/api/webhooks/..."
                    value={formData.webhook_url}
                    onChange={(e) => handleInputChange('webhook_url', e.target.value)}
                    className="py-2"
                    required
                  />
                  <Form.Text className="text-muted">
                    To get a webhook URL: Discord Server → Server Settings → Integrations → Webhooks → New Webhook
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Events to Notify</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    {['error', 'warning', 'info', 'success', 'system'].map((event) => (
                      <Form.Check
                        key={event}
                        type="checkbox"
                        id={`event-${event}`}
                        label={event.charAt(0).toUpperCase() + event.slice(1)}
                        checked={formData.events?.includes(event)}
                        onChange={() => handleEventToggle(event)}
                        className="mb-1"
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    Select which system events should trigger notifications
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Status</Form.Label>
                  <Form.Select 
                    value={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                    className="py-2"
                  >
                    <option value={true}>✅ Active</option>
                    <option value={false}>❌ Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowModal(false)} 
              disabled={submitting}
              className="px-4"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingSubscription ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingSubscription ? 'Update Webhook' : 'Add Webhook'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold text-danger">
            <AlertTriangle size={24} className="me-2" />
            Delete Discord Webhook
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="text-center mb-3">
            <div className="bg-danger bg-opacity-10 rounded-circle d-inline-flex p-3 mb-3">
              <Trash2 size={32} className="text-danger" />
            </div>
            <h5 className="fw-bold">Are you sure you want to delete this webhook?</h5>
            {subscriptionToDelete && (
              <div className="mt-3 p-3 bg-light rounded">
                <div className="fw-medium">
                  <Badge bg="primary" className="me-2">
                    Discord
                  </Badge>
                  <span className="text-break">{subscriptionToDelete.webhook_url}</span>
                </div>
                <div className="mt-2">
                  <Badge bg={subscriptionToDelete.is_active ? 'success' : 'secondary'} className="me-1">
                    {subscriptionToDelete.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="mt-2 small">
                  Events: {subscriptionToDelete.event_types?.join(', ')}
                </div>
              </div>
            )}
            <p className="text-muted mt-3">
              This action cannot be undone. The webhook will be permanently removed.
            </p>
            <Alert variant="warning" className="mt-3">
              <AlertTriangle size={16} className="me-2" />
              You will no longer receive Discord notifications from this webhook.
            </Alert>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 justify-content-center">
          <Button 
            variant="outline-secondary" 
            onClick={handleDeleteCancel}
            className="px-4"
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            className="px-4"
          >
            <Trash2 size={16} className="me-2" />
            Delete Webhook
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Notifications;